import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { SubmissionError } from 'redux-form';
import moment from 'moment';
import { Helmet } from 'react-helmet';
import Checkout from '../common/forms/Checkout';
import Loading from '../common/components/Loading';
import { getUser } from '../redux/actions';
import { cancelSubscription, createSubscription, updateSubscription, updateSource } from './redux/actions';
import getDecodedToken from '../common/auth/utils/getDecodedToken';
import {
  selectSubscriptions,
  selectDefaultSource,
  selectActiveSubscription,
  selectStripeCustomer,
} from './redux/selectors';
import { sendIntercomEvent } from '../common/events';
import plans from '../common/plans';

class BillingForm extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    subscriptions: PropTypes.array,
    courses: PropTypes.object,
    source: PropTypes.object,
  };

  state = {
    paymentMethodFormVisible: false,
    selectedPlan: Object.keys(plans).find(plan => !!plans[plan].default),
  };

  handleUncancel = () => {
    const { activeSubscription, dispatch } = this.props;
    const userId = getDecodedToken().sub;

    dispatch(updateSubscription(userId, activeSubscription.id, undefined, activeSubscription.plan)).then(() => {
      sendIntercomEvent('uncancel_subscription');
    });
  };

  handleCancel = () => {
    const userId = getDecodedToken().sub;

    this.props.dispatch(cancelSubscription(userId, this.props.activeSubscription.id)).then(() =>
      sendIntercomEvent('cancel_subscription', {
        subscription_end: this.props.activeSubscription.subscription_end,
      })
    );
  };

  handleUpdate = values => {
    const userId = getDecodedToken().sub;
    return new Promise((resolve, reject) => {
      window.Stripe.card.createToken(
        {
          name: values.name,
          number: values.number,
          cvc: values.cvc,
          exp_month: values.exp_month,
          exp_year: values.exp_year,
          address_zip: values.postal_code,
        },
        (status, response) => {
          if (response.error) {
            reject(new SubmissionError({ [response.error.param || '_error']: response.error.message }));
          } else {
            resolve(response.id);
          }
        }
      );
    })
      .then(paymentSource => this.props.dispatch(updateSource(userId, paymentSource)))
      .then(action => {
        if (!action.response.ok) {
          throw new SubmissionError({ _error: action.json.message });
        } else {
          this.setState({ paymentMethodFormVisible: false });
          this.props.dispatch(getUser(userId));
        }
      });
  };

  handleCreate = values => {
    const userId = getDecodedToken().sub;
    return new Promise((resolve, reject) => {
      window.Stripe.card.createToken(
        {
          name: values.name,
          number: values.number,
          cvc: values.cvc,
          exp_month: values.exp_month,
          exp_year: values.exp_year,
          address_zip: values.postal_code,
        },
        (status, response) => {
          if (response.error) {
            reject(new SubmissionError({ [response.error.param || '_error']: response.error.message }));
          } else {
            resolve(response.id);
          }
        }
      );
    })
      .then(paymentSource =>
        this.props.dispatch(createSubscription(userId, paymentSource, plans[this.state.selectedPlan].stripeId))
      )
      .then(action => {
        if (!action.response.ok) {
          throw new SubmissionError({ _error: action.json.message });
        } else {
          this.props.dispatch(getUser(userId));
        }
      });
  };

  handlePlanChange = e => {
    this.setState({ selectedPlan: e.target.value });
  };

  renderNoActiveSubscription = () => {
    let message;
    if (this.props.subscriptions.length === 0) {
      message = (
        <span className="billing__no-active-subscription">
          Sorry! This card has been declined. Try a different card or if you believe this was a mistake, contact
          customer service at <a href="mailto:support@tathatagolf.com">support@tathatagolf.com</a>.
        </span>
      );
    } else {
      message = 'Your subscription has been cancelled.';
    }
    return (
      <div>
        <div className="billing__account-notification billing__account-notification--payment-failure">{message}</div>
        <p className="billing__account-notification--new-card-info">Please enter new card information</p>
        <div className="billing__account-notification--new-card-info">
          {Object.keys(plans).map(plan => (
            <p key={plan}>
              <label>
                <input
                  type="radio"
                  value={plan}
                  checked={this.state.selectedPlan === plan}
                  onChange={this.handlePlanChange}
                />
                <strong>{`$${(plans[plan].basePrice / 100).toFixed(2)}`}</strong> {plans[plan].label}
              </label>
            </p>
          ))}
        </div>
        <Checkout onSubmit={this.handleCreate} />
      </div>
    );
  };

  renderStripeStatus = activeStripeSubscription => {
    const { activeSubscription } = this.props;

    const endDate = moment(activeSubscription.subscription_end)
      .utc()
      .format('l');

    let price = activeStripeSubscription.plan.amount;
    let billingSummary =
      activeStripeSubscription.status === 'trialing'
        ? 'Your membership includes a 7-Day Free Trial. After 7 days, you will be charged '
        : '';

    // Adjust price based on discount
    if (activeStripeSubscription.discount) {
      price = Math.max(
        activeStripeSubscription.discount.coupon.amount_off
          ? price - activeStripeSubscription.discount.coupon.amount_off
          : price * ((100 - activeStripeSubscription.discount.coupon.percent_off) / 100),
        0
      );
    }

    // Adjust text based on coupon existence and duration
    if (activeStripeSubscription.discount && activeStripeSubscription.discount.coupon.duration === 'once') {
      billingSummary += `$${(price / 100).toFixed(2)} for the first billing period, and $${(activeStripeSubscription
        .plan.amount / 100).toFixed(2)} each period thereafter.`;
    } else {
      billingSummary += `$${(price / 100).toFixed(2)}, recurring every ${activeStripeSubscription.plan.interval}.`;
    }

    if (activeSubscription.cancel_at_period_end) {
      return (
        <div>
          <p>{billingSummary}</p>
          <div className="billing__account-notification billing__account-notification--cancelled">
            You have cancelled your subscription and it will end on {endDate}
          </div>
          <button type="button" className="billing__button" onClick={this.handleUncancel}>
            Uncancel
          </button>
        </div>
      );
    }

    if (activeSubscription.status === 'past_due') {
      return (
        <div>
          <p>{billingSummary}</p>
          <div className="billing__account-notification billing__account-notification--payment-failure">
            Your payment failed to process, please update your card
          </div>
          <button type="button" className="billing__button" onClick={this.handleCancel}>
            Cancel
          </button>
        </div>
      );
    }

    return (
      <div>
        <p>{billingSummary}</p>
        <div className="billing__account-notification">Your account will renew on {endDate}</div>
        <button type="button" className="billing__button" onClick={this.handleCancel}>
          Cancel {activeSubscription.status === 'trialing' ? 'Trial' : 'Subscription'}
        </button>
        <br />
        <br />
        {activeStripeSubscription.plan.interval !== 'year' && (
          <p>
            To change your plan, and save 20% per year by switching to yearly, contact{' '}
            <a href="mailto:support@tathatagolf.com">support@tathatagolf.com</a>
          </p>
        )}
      </div>
    );
  };

  renderPaidCourses = () => {
    const { courses } = this.props;

    if (!courses.size) {
      return null;
    }

    const paidCourses = courses.filter(course => !!course.get('_pivot_charge_id'));

    return (
      <div className="paper billing__paper">
        <h3>Paid Courses</h3>
        {paidCourses.size === 0 ? (
          <div>
            <p>You have not purchased any courses.</p>
          </div>
        ) : (
          paidCourses.map(course => (
            <div className="paid-course__item" key={course.get('id')}>
              <Link to={`/subscription/courses/${course.get('id')}`}>
                {course.get('name')} (Purchased{' '}
                {moment(course.get('_pivot_date_enrolled'))
                  .utc()
                  .format('l')})
              </Link>
            </div>
          ))
        )}
      </div>
    );
  };

  renderSubscriptionDetails = () => {
    const { activeSubscription, customer } = this.props;
    const activeStripeSubscription =
      activeSubscription &&
      customer.subscriptions.data.find(sub => sub.id === activeSubscription.stripe_subscription_id);

    let details;

    if (!activeSubscription || !activeStripeSubscription) {
      details = this.renderNoActiveSubscription();
    } else if (activeSubscription.ios_iap_transaction_id) {
      details = (
        <p>
          <a href="https://buy.itunes.apple.com/WebObjects/MZFinance.woa/wa/manageSubscriptions" target="_blank">
            Manage your subscription on iTunes
          </a>
        </p>
      );
    } else if (activeSubscription.stripe_subscription_id) {
      details = this.renderStripeStatus(activeStripeSubscription);
    } else {
      details = <p>Contact support to manage your subscription.</p>;
    }

    return (
      <div className="paper billing__paper">
        <h3>Subscription Details</h3>
        {details}
      </div>
    );
  };

  renderPaymentMethod = () => {
    const { source, activeSubscription } = this.props;

    if (!activeSubscription) {
      return null;
    }

    return (
      <div className="paper billing__paper">
        <h3>Payment Method</h3>
        {!source ? (
          <div className="billing__cc-info">
            <p>No credit card on file. </p>
            <button
              type="button"
              className="billing__button"
              onClick={() => this.setState({ paymentMethodFormVisible: true })}
            >
              Add
            </button>
          </div>
        ) : (
          <div className="billing__cc-info">
            <p>
              {source.get('brand')} ending in {source.get('last4')}{' '}
            </p>
            <button
              type="button"
              className="billing__button"
              onClick={() => this.setState({ paymentMethodFormVisible: true })}
            >
              Change
            </button>
          </div>
        )}
        <div
          className="update-payment-form"
          style={{ display: `${this.state.paymentMethodFormVisible === false ? 'none' : 'block'}` }}
        >
          <Checkout onSubmit={this.handleUpdate} onCancel={() => this.setState({ paymentMethodFormVisible: false })} />
        </div>
      </div>
    );
  };

  render() {
    if (this.props.userLoading) {
      return <Loading />;
    }

    if (this.props.isStripeDisabled) {
      return (
        <div className="billing container">
          <Helmet>
            <title>Tathata Golf - Billing</title>
          </Helmet>
          <div className="paper billing__paper">
            <h3>Temporarily Unavailable</h3>
            <p>Billing is temporarily unavailable. Please try again soon.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="billing container">
        <Helmet>
          <title>Tathata Golf - Billing</title>
        </Helmet>
        {this.renderPaymentMethod()}
        {this.renderSubscriptionDetails()}
        {this.renderPaidCourses()}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  activeSubscription: selectActiveSubscription(state),
  subscriptions: selectSubscriptions(state),
  courses: state.subscription.user.getIn(['data', 'courses']),
  source: selectDefaultSource(state),
  customer: selectStripeCustomer(state),
  userLoading: state.subscription.user.get('loading'),
  isStripeDisabled: state.subscription.user.getIn(['data', 'isStripeDisabled']),
});

export default connect(mapStateToProps)(BillingForm);



// WEBPACK FOOTER //
// ./src/subscription/BillingForm.js